import React, { useEffect, useState } from "react";
import {
  Modal, Form, Button, Dropdown, Input, Select, Switch, Tag, message,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PhoneOutlined, EnvironmentOutlined, PoweroffOutlined, MoreOutlined,
} from "@ant-design/icons";
import {
  getVaccinationCentersApi,
  createVaccinationCenterApi,
  updateVaccinationCenterApi,
  deleteVaccinationCenterApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const TYPE_OPTIONS = [
  { value: "Government Hospital", label: "Government Hospital" },
  { value: "Private Hospital",    label: "Private Hospital"    },
  { value: "Clinic",              label: "Clinic"              },
  { value: "PHC",                 label: "PHC (Primary Health Centre)" },
];

const STATE_OPTIONS = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
  "Uttarakhand","West Bengal","Delhi","Jammu & Kashmir","Ladakh",
].map(s => ({ value: s, label: s }));

const ACCENT = "#534ab7";

export default function VaccinationCenters() {
  const [centers,   setCenters]   = useState([]);
  const [search,    setSearch]    = useState("");
  const [typeFilter,setTypeFilter]= useState("All");
  const [open,      setOpen]      = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [form] = Form.useForm();

  const fetchAll = async () => {
    try {
      const res = await getVaccinationCentersApi();
      setCenters((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load vaccination centers");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, type: "Government" });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ ...record });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editing) {
        await updateVaccinationCenterApi(editing.id, values);
        message.success("Center updated");
      } else {
        await createVaccinationCenterApi(values);
        message.success("Center added");
      }
      setOpen(false);
      fetchAll();
    } catch (e) {
      if (e?.errorFields) return;
      message.error("Failed to save center");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateVaccinationCenterApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Center deactivated" : "Center activated");
      fetchAll();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vaccination center?")) return;
    try {
      await deleteVaccinationCenterApi(id);
      message.success("Deleted");
      fetchAll();
    } catch {
      message.error("Failed to delete");
    }
  };

  const filtered = centers.filter((c) => {
    const matchType = typeFilter === "All" || c.type?.toLowerCase().includes(typeFilter.toLowerCase());
    const q = search.toLowerCase();
    const matchSearch = !q || c.name?.toLowerCase().includes(q)
      || c.city?.toLowerCase().includes(q)
      || c.pincode?.includes(q)
      || c.state?.toLowerCase().includes(q);
    return matchType && matchSearch;
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (name, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.address}</div>
        </div>
      ),
    },
    {
      title: "Location",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.city}{r.state ? `, ${r.state}` : ""}</div>
          {r.pincode && <div style={{ fontSize: 12, color: "#888" }}>📍 {r.pincode}</div>}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (t) => (
        <Tag color={t === "Government" ? "green" : "blue"}>{t || "—"}</Tag>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      render: (p) => p ? <span><PhoneOutlined /> {p}</span> : "—",
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (a) => <Tag color={a ? "success" : "default"}>{a ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>💉 Vaccination Centers</h2>
          <p style={{ margin: 0, color: "#888", fontSize: 13 }}>
            Manage government and private vaccination centers visible in the app
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: ACCENT }}
          onClick={openAdd}>
          Add Center
        </Button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Input.Search
          placeholder="Search by name, city, pincode…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
          allowClear
        />
        <div style={{ display: "flex", gap: 6 }}>
          {["All", "Government", "Private"].map((t) => (
            <Button
              key={t}
              type={typeFilter === t ? "primary" : "default"}
              size="small"
              style={typeFilter === t ? { background: ACCENT } : {}}
              onClick={() => setTypeFilter(t)}
            >
              {t}
            </Button>
          ))}
        </div>
        <div style={{ marginLeft: "auto", color: "#888", fontSize: 13, alignSelf: "center" }}>
          {filtered.length} center{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      <AppTable columns={columns} data={filtered} rowKey="id" />

      {/* Add / Edit Modal */}
      <Modal
        title={editing ? "Edit Vaccination Center" : "Add Vaccination Center"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSave}
        confirmLoading={loading}
        width={600}
        okText={editing ? "Update" : "Add Center"}
        okButtonProps={{ style: { background: ACCENT } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Center Name" rules={[{ required: true }]}>
            <AppInput placeholder="e.g. Primary Health Centre, Andheri" />
          </Form.Item>

          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <AppSelect options={TYPE_OPTIONS} placeholder="Select type" />
          </Form.Item>

          <Form.Item name="address" label="Full Address">
            <Input.TextArea rows={2} placeholder="Street address, building, landmark" />
          </Form.Item>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Form.Item name="city" label="City" rules={[{ required: true }]}>
              <AppInput placeholder="City" />
            </Form.Item>
            <Form.Item name="state" label="State">
              <AppSelect options={STATE_OPTIONS} placeholder="State" />
            </Form.Item>
            <Form.Item name="pincode" label="Pincode">
              <AppInput placeholder="6-digit pincode" maxLength={6} />
            </Form.Item>
          </div>

          <Form.Item name="phone" label="Phone Number">
            <AppInput placeholder="+91 XXXXX XXXXX" />
          </Form.Item>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="lat" label="Latitude (optional)">
              <AppInput placeholder="e.g. 19.0760" />
            </Form.Item>
            <Form.Item name="lng" label="Longitude (optional)">
              <AppInput placeholder="e.g. 72.8777" />
            </Form.Item>
          </div>

          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
